import React from 'react';
import { Typography, Container, Box, Button, Grid, Card, CardContent } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import cover from '../assets/cover.jpg';
import thermopiedLogo from '../assets/product/thermopied.jpg';
import { useLanguage } from '../LanguageContext';
import { translations } from '../translations';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const HeroSection = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: '70vh',
    minHeight: '400px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(8),
    [theme.breakpoints.down('sm')]: {
        height: 'auto',
        minHeight: '300px',
        marginBottom: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.05) 100%)',
        zIndex: 1,
    }
}));

const HeroImage = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'center center',
        animation: 'ken-burns 20s ease-in-out infinite alternate',
    },
    [theme.breakpoints.down('sm')]: {
        '& img': {
            objectPosition: 'center center',
        }
    },
    '@keyframes ken-burns': {
        '0%': {
            transform: 'scale(1)',
        },
        '100%': {
            transform: 'scale(1.1)',
        }
    }
}));

const HeroContent = styled(Box)(({ theme }) => ({
    position: 'relative',
    zIndex: 2,
    textAlign: 'center',
    color: '#fff',
    padding: theme.spacing(4),
    animation: `${fadeIn} 1s ease-out`,
}));

const FeatureCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    borderRadius: theme.spacing(2),
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    border: '1px solid rgba(0,0,0,0.05)',
    '&:hover': {
        transform: 'translateY(-12px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        borderColor: theme.palette.primary.main,
    }
}));

const HomeView = () => {
    const { language } = useLanguage();
    const t = translations[language].product;

    return (
        <Box>
            {/* Hero Section */}
            <HeroSection>
                <HeroImage>
                    <img src={cover} alt="ThermoPied Ski Boot Heater" />
                </HeroImage>
                <HeroContent>
                    <Typography
                        variant="h4"
                        sx={{
                            mb: { xs: 3, sm: 5 },
                            fontWeight: 400,
                            textShadow: '2px 2px 4px rgba(0,0,0,0.4)',
                            maxWidth: '900px',
                            mx: 'auto',
                            fontSize: { xs: '1.5rem', md: '2rem' },
                            lineHeight: 1.4
                        }}
                    >
                        {t.subtitle}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            href="/product"
                            sx={{
                                px: { xs: 2.5, md: 4 },
                                py: { xs: 1, md: 1.5 },
                                fontSize: { xs: '0.9rem', md: '1.1rem' },
                                fontWeight: 600,
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                                boxShadow: '0 4px 10px rgba(21, 101, 192, 0.4)',
                                textTransform: 'none',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 15px rgba(21, 101, 192, 0.5)',
                                    background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
                                }
                            }}
                        >
                            {t.learnMore}
                        </Button>
                    </Box>
                </HeroContent>
            </HeroSection>

            {/* Features Section */}
            <Container maxWidth="lg" sx={{ mb: { xs: 4, sm: 10 }, px: 2 }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 8 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box
                            component="img"
                            src={thermopiedLogo}
                            alt={t.whyChoose || 'ThermoPied'}
                            sx={{ maxWidth: { xs: '200px', md: '320px' }, width: '100%', height: 'auto' }}
                        />
                    </Box>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
                        {t.tagline}
                    </Typography>
                </Box>

                <Grid container spacing={{ xs: 2, sm: 4 }} sx={{ alignItems: 'stretch' }}>
                    <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ display: 'flex' }}>
                        <FeatureCard sx={{ height: '100%', width: '100%' }}>
                            <CardContent sx={{ flexGrow: 1, textAlign: 'left', p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                                    <Box
                                        sx={{
                                            width: { xs: 50, sm: 60 },
                                            height: { xs: 50, sm: 60 },
                                            minWidth: { xs: 50, sm: 60 },
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: 0,
                                            boxShadow: '0 8px 20px rgba(255, 107, 107, 0.3)',
                                        }}
                                    >
                                        <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>🔥</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0, fontSize: '1.1rem', alignSelf: 'center' }}>
                                        {t.features.warmth.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.9rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                    {t.features.warmth.description}
                                </Typography>
                            </CardContent>
                        </FeatureCard>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ display: 'flex' }}>
                        <FeatureCard sx={{ height: '100%', width: '100%' }}>
                            <CardContent sx={{ flexGrow: 1, textAlign: 'left', p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                                    <Box
                                        sx={{
                                            width: { xs: 50, sm: 60 },
                                            height: { xs: 50, sm: 60 },
                                            minWidth: { xs: 50, sm: 60 },
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: 0,
                                            boxShadow: '0 8px 20px rgba(79, 172, 254, 0.3)',
                                        }}
                                    >
                                        <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>🔋</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0, fontSize: '1.1rem', alignSelf: 'center' }}>
                                        {t.features.battery.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.9rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                    {t.features.battery.description}
                                </Typography>
                            </CardContent>
                        </FeatureCard>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ display: 'flex' }}>
                        <FeatureCard sx={{ height: '100%', width: '100%' }}>
                            <CardContent sx={{ flexGrow: 1, textAlign: 'left', p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                                    <Box
                                        sx={{
                                            width: { xs: 50, sm: 60 },
                                            height: { xs: 50, sm: 60 },
                                            minWidth: { xs: 50, sm: 60 },
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #43E97B 0%, #38F9D7 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: 0,
                                            boxShadow: '0 8px 20px rgba(67, 233, 123, 0.3)',
                                        }}
                                    >
                                        <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>💰</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0, fontSize: '1.1rem', alignSelf: 'center' }}>
                                        {t.features.value.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.9rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                    {t.features.value.description}
                                </Typography>
                            </CardContent>
                        </FeatureCard>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ display: 'flex' }}>
                        <FeatureCard sx={{ height: '100%', width: '100%' }}>
                            <CardContent sx={{ flexGrow: 1, textAlign: 'left', p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                                    <Box
                                        sx={{
                                            width: { xs: 50, sm: 60 },
                                            height: { xs: 50, sm: 60 },
                                            minWidth: { xs: 50, sm: 60 },
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #FA709A 0%, #FEE140 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: 0,
                                            boxShadow: '0 8px 20px rgba(250, 112, 154, 0.3)',
                                        }}
                                    >
                                        <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>✓</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0, fontSize: '1.1rem', alignSelf: 'center' }}>
                                        {t.features.reliable.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.9rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                    {t.features.reliable.description}
                                </Typography>
                            </CardContent>
                        </FeatureCard>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ display: 'flex' }}>
                        <FeatureCard sx={{ height: '100%', width: '100%' }}>
                            <CardContent sx={{ flexGrow: 1, textAlign: 'left', p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                                    <Box
                                        sx={{
                                            width: { xs: 50, sm: 60 },
                                            height: { xs: 50, sm: 60 },
                                            minWidth: { xs: 50, sm: 60 },
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: 0,
                                            boxShadow: '0 8px 20px rgba(21, 101, 192, 0.3)',
                                        }}
                                    >
                                        <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>📱</Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0, fontSize: '1.1rem', alignSelf: 'center' }}>
                                        {t.features.mobileApp.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.9rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                    {t.features.mobileApp.description}
                                </Typography>
                            </CardContent>
                        </FeatureCard>
                    </Grid>
                </Grid>
            </Container>

            {/* Reviews Section */}
            <Container maxWidth="lg" sx={{ mb: { xs: 4, sm: 10 }, px: 2 }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 8 } }}>
                    <Typography
                        variant="h3"
                        component="h2"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}
                    >
                        {t.reviews.title}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
                        {t.reviews.subtitle}
                    </Typography>
                </Box>

                <Grid container spacing={{ xs: 2, sm: 4 }} sx={{ alignItems: 'stretch' }}>
                    {t.reviews.testimonials.map((review, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 6 }} key={index} sx={{ display: 'flex' }}>
                            <FeatureCard sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                                                {review.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {review.location}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: '#FFD700', fontSize: '1rem', flexShrink: 0 }}>
                                            {'⭐'.repeat(review.rating)}
                                        </Box>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.6,
                                            fontStyle: 'italic',
                                            flex: 1,
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            hyphens: 'auto'
                                        }}
                                    >
                                        "{review.text}"
                                    </Typography>
                                </CardContent>
                            </FeatureCard>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default HomeView;
